
import React, { useState, useEffect, useRef } from "react";
import { useParams, useNavigate, useSearchParams, useLocation } from "react-router-dom";
import { products } from "../../data/productData";
import { useCart } from "../../contexts/CartContext";
import { useFavorites } from "../../contexts/FavoritesContext";
import { useAuth } from "../../contexts/AuthContext";
import { toast } from "sonner";
import { ADD_TO_CART_TOAST_DURATION } from "../../lib/toastConfig";
import { supabase } from "../../lib/supabase";
import Seo from "../seo/Seo";
import { trackViewItem } from "../../lib/analytics";
import ProductPrice from "./ProductPrice";
import { getProductPricing, withEffectivePricing } from "../../utils/productPricing";
import { isProductVisible } from "../../utils/productFilters";
import { getProductPath, resolveProductByIdentifier } from "../../utils/productLinks";

const StarRating = ({ rating }) => (
  <div className="flex items-center">
    {[1, 2, 3, 4, 5].map((star) => (
      <svg key={star} className={`w-4 h-4 ${star <= rating ? "text-amber-400" : "text-gray-600"} fill-current`} viewBox="0 0 20 20">
        <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
      </svg>
    ))}
  </div>
);

const StarRatingInput = ({ value, onChange }) => (
  <div className="flex items-center gap-1">
    {[1, 2, 3, 4, 5].map((star) => (
      <button key={star} type="button" onClick={() => onChange(star)} className="focus:outline-none">
        <svg className={`w-6 h-6 transition-colors ${star <= value ? "text-amber-400" : "text-gray-600"}`} fill="currentColor" viewBox="0 0 20 20">
          <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
        </svg>
      </button>
    ))}
  </div>
);

const AccordionSection = ({ title, children }) => {
  const [open, setOpen] = useState(false);
  return (
    <div className="border-t border-gray-200">
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between py-3 text-left"
      >
        <span className="text-xs font-semibold uppercase tracking-wider text-gray-800">{title}</span>
        <svg className={`w-4 h-4 text-gray-500 transition-transform duration-200 ${open ? "rotate-180" : ""}`} fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
        </svg>
      </button>
      {open && <div className="pb-4 text-sm text-gray-600 leading-relaxed">{children}</div>}
    </div>
  );
};

const maskEmail = (email) => {
  if (!email) return "Anonymous";
  const [local, domain] = email.split("@");
  if (local.length <= 2) return local[0] + "***@" + domain;
  return local.slice(0, 3) + "***" + local.slice(-1) + "@" + domain;
};

const ProductPage = ({ productId } = {}) => {
  const params = useParams();
  const id = productId ?? params.id;
  const navigate = useNavigate();
  const location = useLocation();
  const { addToCart } = useCart();
  const { toggleFavorite, isFavorite } = useFavorites();
  const { user } = useAuth();
  const [searchParams] = useSearchParams();

  const wrapperRef = useRef(null);
  const infoColumnRef = useRef(null);
  const reviewFormRef = useRef(null);
  const [wrapperH, setWrapperH] = useState(null);

  const [quantity, setQuantity] = useState(1);
  const [selectedColor, setSelectedColor] = useState(null);
  const [selectedImage, setSelectedImage] = useState(0);
  const [selectedMedia, setSelectedMedia] = useState("image");
  const [isZoomed, setIsZoomed] = useState(false);
  const [activeTab, setActiveTab] = useState("info");
  const [likesCount, setLikesCount] = useState(0);
  const [hasLiked, setHasLiked] = useState(false);
  const [reviews, setReviews] = useState([]);
  const [newReview, setNewReview] = useState({ comment: "", rating: 5 });
  const [guestInfo, setGuestInfo] = useState({ name: "", email: "", website: "" });
  const [submittingReview, setSubmittingReview] = useState(false);

  const product = resolveProductByIdentifier(products, id);

  useEffect(() => {
    if (product?.slug && id !== product.slug) {
      navigate(`${getProductPath(product)}${location.search}`, { replace: true });
    }
  }, [id, location.search, navigate, product]);

  const openReviewForm = () => {
    setActiveTab("reviews");
    // Scroll the inner info column directly — scrollIntoView() walks up to
    // the window, which fights the desktop overflow:hidden lock above and
    // leaves the page stuck.
    requestAnimationFrame(() => {
      const column = infoColumnRef.current;
      const form = reviewFormRef.current;
      if (!column || !form) return;
      const offset = form.getBoundingClientRect().top - column.getBoundingClientRect().top + column.scrollTop;
      column.scrollTo({ top: offset, behavior: "smooth" });
    });
  };

  const copyReviewLink = async () => {
    if (!product) return;
    const origin =
      typeof window !== "undefined" ? window.location.origin : "https://www.jmolux.com";
    const reviewUrl = `${origin}${getProductPath(product)}?review=1`;

    try {
      await navigator.clipboard.writeText(reviewUrl);
      toast.success("Review link copied");
    } catch (error) {
      toast.error("Could not copy review link");
    }
  };

  // Deep link support: ?review=1 jumps straight into the review form
  // (used by the "Rate & Review" share-link flow) so it works in one tap.
  useEffect(() => {
    if (searchParams.get("review") === "1") {
      openReviewForm();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Lock page scroll on desktop and measure exact available height
  useEffect(() => {
    let prevOverflow = "";
    const measure = () => {
      if (!wrapperRef.current) return;
      const isDesktop = window.innerWidth >= 1024;
      const top = wrapperRef.current.getBoundingClientRect().top;
      if (isDesktop) {
        setWrapperH(window.innerHeight - top);
        if (document.body.style.overflow !== "hidden") {
          prevOverflow = document.body.style.overflow;
          document.body.style.overflow = "hidden";
        }
      } else {
        setWrapperH(null);
        document.body.style.overflow = prevOverflow;
        prevOverflow = "";
      }
    };
    const t = setTimeout(measure, 30);
    window.addEventListener("resize", measure);
    return () => {
      clearTimeout(t);
      window.removeEventListener("resize", measure);
      document.body.style.overflow = prevOverflow || "";
    };
  }, []);

  useEffect(() => {
    if (!product) return;
    const loadLikes = async () => {
      const { count } = await supabase.from("product_likes").select("*", { count: "exact", head: true }).eq("product_id", product.id);
      setLikesCount(count || 0);
      if (user) {
        const { data } = await supabase.from("product_likes").select("*").eq("product_id", product.id).eq("user_id", user.id).maybeSingle();
        setHasLiked(!!data);
      }
    };
    const loadReviews = async () => {
      const { data, error } = await supabase.from("product_reviews").select("*").eq("product_id", product.id).eq("is_approved", true).order("created_at", { ascending: false });
      if (!error) setReviews(data);
    };
    loadLikes();
    loadReviews();
  }, [product, user]);

  useEffect(() => {
    if (product) trackViewItem(product);
  }, [product]);

  useEffect(() => {
    if (product?.hasColors && product.colors.length > 0) setSelectedColor(product.colors[0]);
  }, [product]);

  const toggleLike = async () => {
    if (!user) return toast.info("Login to like products");
    if (hasLiked) {
      await supabase.from("product_likes").delete().eq("user_id", user.id).eq("product_id", product.id);
      setHasLiked(false);
      setLikesCount((p) => p - 1);
    } else {
      await supabase.from("product_likes").insert({ user_id: user.id, product_id: product.id });
      setHasLiked(true);
      setLikesCount((p) => p + 1);
    }
  };

  const submitReview = async () => {
    if (!newReview.comment.trim()) return toast.error("Review cannot be empty");
    setSubmittingReview(true);
    try {
      if (user) {
        const { error } = await supabase.from("product_reviews").insert({
          product_id: product.id, user_id: user.id, author: user.email || "Anonymous",
          rating: newReview.rating, comment: newReview.comment, is_approved: false,
        });
        if (error) toast.error("Failed to submit review");
        else { toast.success("Review submitted!"); setNewReview({ comment: "", rating: 5 }); }
        return;
      }

      if (!guestInfo.name.trim()) return toast.error("Please enter your name");
      if (!guestInfo.email.trim()) return toast.error("Please enter your email");

      const res = await fetch("/api/reviews", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          product_id: product.id,
          rating: newReview.rating,
          comment: newReview.comment,
          name: guestInfo.name,
          email: guestInfo.email,
          website: guestInfo.website,
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        toast.error(data.error || "Failed to submit review");
        return;
      }
      toast.success("Review submitted!");
      setNewReview({ comment: "", rating: 5 });
      setGuestInfo({ name: "", email: "", website: "" });
    } finally {
      setSubmittingReview(false);
    }
  };

  const handleAddToCart = () => {
    const item = withEffectivePricing({
      ...product,
      selectedColor,
      cartId: product.hasColors ? `${product.id}-${selectedColor?.value}` : product.id,
    });
    for (let i = 0; i < quantity; i++) addToCart(item);
    toast.success(`Added ${quantity > 1 ? `${quantity}× ` : ""}${product.name} to cart`, {
      duration: ADD_TO_CART_TOAST_DURATION,
    });
  };

  const seoDescription = (product?.detailedDescription || product?.description || "").replace(/\s+/g, " ").trim().slice(0, 155);

  if (!product || !isProductVisible(product)) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-gray-900 to-black flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-white mb-4">Product Not Found</h1>
          <button onClick={() => navigate("/")} className="px-6 py-2 rounded-xl bg-amber-400 text-black font-semibold text-sm">Return to Home</button>
        </div>
      </div>
    );
  }

  const images = product.images && product.images.length > 0 ? product.images : [product.image];
  const isProductFavorited = isFavorite(product.id);

  return (
    <>
      <Seo
        title={product.name}
        description={seoDescription}
        path={getProductPath(product)}
        image={product.image}
        type="product"
        structuredData={{
          "@context": "https://schema.org", "@type": "Product",
          name: product.name, image: product.images || [product.image],
          description: product.detailedDescription || product.description,
          sku: product.sku, category: product.mainCategories || product.category,
          brand: { "@type": "Brand", name: "JmoLux" },
          offers: { "@type": "Offer", priceCurrency: "USD", price: getProductPricing(product).effectivePrice,
            availability: product.inStock ? "https://schema.org/InStock" : "https://schema.org/OutOfStock" },
        }}
      />

      {/* Page wrapper — height is measured dynamically so it fills exactly the viewport below the sticky header */}
      <div
        ref={wrapperRef}
        className="bg-gray-50 flex flex-col overflow-hidden"
        style={wrapperH ? { height: `${wrapperH}px` } : undefined}
      >
        {/* Breadcrumb — fixed height strip */}
        <div className="flex-shrink-0 border-b border-gray-200 bg-white">
          <div className="container mx-auto px-4 py-2.5 flex items-center gap-2 text-xs text-gray-400">
            <button onClick={() => navigate("/")} className="hover:text-amber-500 transition-colors">Home</button>
            <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" /></svg>
            <span className="text-gray-600 truncate">{product.name}</span>
          </div>
        </div>

        {/* Main content — two-column, fills all remaining space */}
        <div className="flex-1 min-h-0 container mx-auto px-4 py-4 overflow-hidden">
          <div className="h-full flex flex-col lg:flex-row gap-5 lg:gap-8">

            {/* ── LEFT: Image Gallery ── */}
            <div className="lg:w-[44%] flex-shrink-0 flex flex-col gap-2 overflow-hidden">
              {/* Main image — capped height so it doesn't dominate */}
              <div
                className="relative rounded-2xl overflow-hidden bg-gray-100 group cursor-zoom-in border border-gray-200 flex items-center justify-center"
                style={{ aspectRatio: "1 / 1", maxHeight: "58vh" }}
                onClick={() => setIsZoomed((z) => !z)}
              >
                {product.videoUrl && selectedMedia === "video" ? (
                  <video className="w-full h-full object-contain" controls playsInline preload="metadata">
                    <source src={product.videoUrl} type="video/mp4" />
                  </video>
                ) : (
                  <img
                    src={images[selectedImage]}
                    alt={product.name}
                    loading="eager"
                    fetchPriority="high"
                    decoding="async"
                    className={`max-w-full max-h-full w-full h-full object-contain transition-transform duration-500 ${isZoomed ? "scale-110" : ""}`}
                  />
                )}

                {/* Status badges */}
                <div className="absolute top-2 left-2 flex flex-col gap-0.5 pointer-events-none">
                  {product.isSale && (
                    <span className="bg-red-600 text-white text-[9px] font-semibold uppercase tracking-wide px-1.5 py-0.5 rounded-md shadow-sm leading-none">
                      {product.salePercent ? `−${product.salePercent}%` : "Sale"}
                    </span>
                  )}
                  {product.isNew && <span className="bg-amber-400 text-black text-[9px] font-semibold uppercase tracking-wide px-1.5 py-0.5 rounded-md shadow-sm leading-none">New</span>}
                  {product.isBestSeller && <span className="bg-black/80 text-amber-300 text-[9px] font-semibold uppercase tracking-wide px-1.5 py-0.5 rounded-md shadow-sm border border-amber-400/30 leading-none">Best Seller</span>}
                </div>

                {/* Zoom hint */}
                <span className="absolute bottom-3 right-3 text-[9px] uppercase tracking-widest text-white/50 bg-black/40 px-2 py-1 rounded-md opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none">
                  {isZoomed ? "Click to zoom out" : "Click to zoom"}
                </span>
              </div>

              {/* Thumbnail strip — fixed height */}
              <div className="flex-shrink-0 flex gap-2 overflow-x-auto [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
                {product.videoUrl && (
                  <button
                    onClick={() => setSelectedMedia("video")}
                    className={`relative flex-shrink-0 h-14 w-14 rounded-xl overflow-hidden border-2 transition-all duration-150 ${selectedMedia === "video" ? "border-amber-400 ring-2 ring-amber-400/30" : "border-gray-200 hover:border-amber-400/60"}`}
                  >
                    <div className="absolute inset-0 bg-black/50 flex items-center justify-center">
                      <svg className="w-4 h-4 text-white" fill="currentColor" viewBox="0 0 24 24"><path d="M8 5v14l11-7z" /></svg>
                    </div>
                  </button>
                )}
                {images.map((img, i) => (
                  <button
                    key={i}
                    onClick={() => { setSelectedMedia("image"); setSelectedImage(i); }}
                    className={`flex-shrink-0 h-14 w-14 rounded-xl overflow-hidden border-2 transition-all duration-150 ${selectedMedia === "image" && selectedImage === i ? "border-amber-400 ring-2 ring-amber-400/30" : "border-gray-200 hover:border-amber-400/60"}`}
                  >
                    <img src={img} alt="" loading="lazy" className="w-full h-full object-contain bg-gray-50" />
                  </button>
                ))}
              </div>
            </div>

            {/* ── RIGHT: Product Info — scrollable within column ── */}
            <div ref={infoColumnRef} className="flex-1 min-h-0 overflow-y-auto [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden px-2 pb-5">

              {/* Tabs */}
              <div className="flex gap-0 border-b border-gray-200 mb-4 sticky top-0 bg-gray-50 z-10">
                {[["info", "Overview"], ["reviews", `Reviews (${reviews.length})`]].map(([key, label]) => (
                  <button
                    key={key}
                    onClick={() => setActiveTab(key)}
                    className={`px-5 py-2.5 text-xs font-semibold uppercase tracking-widest border-b-2 -mb-px transition-colors ${activeTab === key ? "border-amber-500 text-amber-600" : "border-transparent text-gray-500 hover:text-gray-800"}`}
                  >
                    {label}
                  </button>
                ))}
              </div>

              {activeTab === "info" && (
                <div className="space-y-3">
                  {/* Category + Likes */}
                  <div className="flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between">
                    <span className="text-[10px] uppercase tracking-[0.2em] text-amber-600 font-medium">
                      {product.category}{product.mainCategories ? ` · ${product.mainCategories}` : ""}
                    </span>
                    <div className="grid grid-cols-2 gap-2 sm:flex sm:flex-wrap sm:items-center sm:justify-end sm:gap-2">
                      <button onClick={openReviewForm} className="inline-flex min-h-9 items-center justify-center gap-1.5 rounded-lg border border-amber-200 bg-amber-50 px-3 text-[11px] font-semibold text-amber-700 transition-colors hover:border-amber-300 hover:bg-amber-100 sm:min-h-0 sm:border-0 sm:bg-transparent sm:px-0 sm:text-xs sm:font-normal sm:text-gray-400 sm:hover:bg-transparent sm:hover:text-amber-500">
                        <svg className="w-3.5 h-3.5" fill="currentColor" viewBox="0 0 20 20">
                          <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                        </svg>
                        Rate &amp; Review
                      </button>
                      <button onClick={copyReviewLink} className="inline-flex min-h-9 items-center justify-center gap-1.5 rounded-lg border border-gray-200 bg-white px-3 text-[11px] font-semibold text-gray-600 shadow-sm transition-colors hover:border-amber-300 hover:text-amber-600 sm:min-h-0 sm:border-0 sm:bg-transparent sm:px-0 sm:text-xs sm:font-normal sm:text-gray-400 sm:shadow-none sm:hover:bg-transparent sm:hover:text-amber-500">
                        <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="2">
                          <path strokeLinecap="round" strokeLinejoin="round" d="M8 7h8M8 11h8M8 15h5M6 3h9l3 3v15H6z" />
                        </svg>
                        Copy Link
                      </button>
                      <button onClick={toggleLike} className={`col-span-2 inline-flex min-h-9 items-center justify-center gap-1.5 rounded-lg border px-3 text-[11px] font-semibold transition-colors sm:col-span-1 sm:min-h-0 sm:border-0 sm:px-0 sm:text-xs sm:font-normal ${hasLiked ? "border-red-200 bg-red-50 text-red-500 sm:bg-transparent" : "border-gray-200 bg-white text-gray-600 shadow-sm hover:border-red-200 hover:text-red-500 sm:bg-transparent sm:text-gray-400 sm:shadow-none"}`}>
                        <svg className="w-3.5 h-3.5" fill={hasLiked ? "currentColor" : "none"} stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                          <path d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.682l-1.318-1.364a4.5 4.5 0 00-6.364 0z" />
                        </svg>
                        {likesCount} likes
                      </button>
                    </div>
                  </div>

                  {/* Title + SKU */}
                  <div>
                    <h1 className="text-xl lg:text-2xl font-bold text-gray-950 leading-snug">{product.name}</h1>
                    {product.sku && <p className="text-[10px] text-gray-400 uppercase tracking-wider mt-0.5">SKU: {product.sku}</p>}
                  </div>

                  {/* Price */}
                  <ProductPrice product={product} size="md" />

                  {/* Description */}
                  <p className="text-xs text-gray-600 leading-snug line-clamp-2">
                    {product.detailedDescription || product.description}
                  </p>

                  {/* Color selector */}
                  {product.hasColors && (
                    <div className="space-y-2">
                      <p className="text-xs text-gray-500 uppercase tracking-wider">
                        Color: <span className="text-gray-900 font-medium">{selectedColor?.name}</span>
                      </p>
                      <div className="flex flex-wrap gap-2">
                        {product.colors.map((color) => (
                          <button
                            key={color.value}
                            onClick={() => setSelectedColor(color)}
                            title={color.name}
                            style={{ backgroundColor: color.hex }}
                            className={`w-8 h-8 rounded-full border-2 transition-all duration-150 ${selectedColor?.value === color.value ? "border-amber-500 ring-2 ring-amber-400/30 scale-110" : "border-gray-300 hover:border-gray-500"}`}
                          >
                            {selectedColor?.value === color.value && (
                              <div className="w-full h-full rounded-full flex items-center justify-center">
                                <svg className="w-3.5 h-3.5 text-white drop-shadow" fill="currentColor" viewBox="0 0 20 20">
                                  <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                </svg>
                              </div>
                            )}
                          </button>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Qty + Add to Cart + Wishlist — single row */}
                  <div className="flex items-center gap-2 pt-1">
                    {/* Qty stepper */}
                    <div className="flex-shrink-0 flex items-center h-11 bg-gray-50 border border-gray-200 rounded-xl overflow-hidden">
                      <button type="button" onClick={() => setQuantity((q) => Math.max(1, q - 1))} className="px-3 h-full text-xs text-gray-500 hover:text-gray-950 hover:bg-gray-100 transition-colors">−</button>
                      <span className="px-3 h-full flex items-center border-x border-gray-200 text-gray-950 text-xs font-medium min-w-[2.25rem] justify-center">{quantity}</span>
                      <button type="button" onClick={() => setQuantity((q) => Math.min(10, q + 1))} className="px-3 h-full text-xs text-gray-500 hover:text-gray-950 hover:bg-gray-100 transition-colors">+</button>
                    </div>
                    {/* Add to Cart */}
                    <button
                      onClick={handleAddToCart}
                      className="flex-1 h-9 sm:h-11 rounded-xl bg-gradient-to-r from-amber-400 to-yellow-500 text-black font-bold text-[10px] sm:text-xs uppercase tracking-wider hover:from-amber-500 hover:to-yellow-600 active:scale-[0.98] transition-all shadow-lg shadow-amber-500/20 flex items-center justify-center gap-1"
                    >
                      <svg className="w-3.5 h-3.5 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2.5}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M16 11V7a4 4 0 00-8 0v4M5 9h14l-1 12H6L5 9z" />
                      </svg>
                      Add to Cart
                    </button>
                    {/* Wishlist icon */}
                    <button
                      onClick={() => toggleFavorite(product)}
                      title={isProductFavorited ? "Remove from wishlist" : "Add to wishlist"}
                      className={`flex-shrink-0 h-11 w-11 rounded-xl border flex items-center justify-center transition-all active:scale-[0.98] ${isProductFavorited ? "border-red-300 bg-red-50 text-red-500" : "border-gray-200 bg-gray-50 text-gray-500 hover:text-red-500 hover:border-red-300"}`}
                    >
                      <svg className="w-4 h-4" fill={isProductFavorited ? "currentColor" : "none"} stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                        <path d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.682l-1.318-1.364a4.5 4.5 0 00-6.364 0z" />
                      </svg>
                    </button>
                  </div>

                  {/* Accordions */}
                  <div className="pt-1">
                    {product.usage && (
                      <AccordionSection title="Usage & Care">
                        <div className="whitespace-pre-line">{product.usage}</div>
                      </AccordionSection>
                    )}
                    {product.specifications && (
                      <AccordionSection title="Specifications">
                        <dl className="grid grid-cols-[auto_1fr] gap-x-5 gap-y-1.5">
                          {Object.entries(product.specifications).map(([k, v]) => (
                            <React.Fragment key={k}>
                              <dt className="text-gray-500 capitalize text-xs">{k.replace(/([A-Z])/g, " $1").trim()}:</dt>
                              <dd className="text-gray-800 text-xs">{v}</dd>
                            </React.Fragment>
                          ))}
                        </dl>
                      </AccordionSection>
                    )}
                    {product.benefits && product.benefits.length > 0 && (
                      <AccordionSection title="Key Benefits">
                        <ul className="space-y-1.5">
                          {product.benefits.map((b) => (
                            <li key={b} className="flex gap-2 items-start text-xs">
                              <span className="text-amber-400 shrink-0 mt-0.5">◆</span>
                              <span>{b}</span>
                            </li>
                          ))}
                        </ul>
                      </AccordionSection>
                    )}
                    {product.ingredients && (
                      <AccordionSection title="Ingredients">
                        <p className="text-xs">{product.ingredients}</p>
                      </AccordionSection>
                    )}
                    {product.warnings && (
                      <AccordionSection title="Warnings">
                        <p className="text-xs">{product.warnings}</p>
                      </AccordionSection>
                    )}
                  </div>

                  {/* ── Suggested Products ── */}
                  {(() => {
                    const currentCat = (product.category || "").toLowerCase().trim();
                    const currentMain = (product.mainCategories || "").toLowerCase().trim();
                    const visibleProducts = products.filter(isProductVisible);
                    const sameCategory = visibleProducts.filter(
                      (p) => p.id !== product.id && (p.category || "").toLowerCase().trim() === currentCat
                    );
                    const sameBroad = visibleProducts.filter(
                      (p) => p.id !== product.id &&
                        (p.mainCategories || "").toLowerCase().trim() === currentMain &&
                        (p.category || "").toLowerCase().trim() !== currentCat
                    );
                    const merged = [...sameCategory, ...sameBroad].slice(0, 6);
                    const fallback = merged.length < 2 ? visibleProducts.filter((p) => p.id !== product.id).slice(0, 6) : merged;
                    if (fallback.length === 0) return null;
                    return (
                      <div className="pt-3 border-t border-gray-200">
                        <p className="text-[10px] uppercase tracking-[0.2em] text-amber-600 font-semibold mb-2">You May Also Like</p>
                        <div className="grid grid-cols-6 gap-2">
                          {fallback.map((p) => (
                            <div key={p.id} className="group flex flex-col min-w-0">
                              <button
                                type="button"
                                onClick={() => navigate(getProductPath(p))}
                                className="relative w-full aspect-square rounded-xl overflow-hidden bg-gray-100 border border-gray-200 group-hover:border-amber-400 transition-all duration-200 shadow-sm group-hover:shadow-md"
                              >
                                <img
                                  src={p.image}
                                  alt={p.name}
                                  loading="lazy"
                                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                                />
                              </button>
                              <p className="mt-1 text-[10px] font-medium text-gray-700 group-hover:text-gray-950 line-clamp-2 leading-snug transition-colors text-center">{p.name}</p>
                              <div className="mt-auto pt-1.5 flex items-center justify-center gap-1.5">
                                <button
                                  type="button"
                                  onClick={() => {
                                    addToCart(p);
                                    toast.success(`${p.name} added to cart`, {
                                      duration: ADD_TO_CART_TOAST_DURATION,
                                    });
                                  }}
                                  aria-label={`Add ${p.name} to cart`}
                                  title="Add to cart"
                                  className="flex h-4 w-4 shrink-0 items-center justify-center rounded-full border border-amber-500/60 text-amber-500 hover:border-amber-500 hover:bg-amber-50 active:scale-90 transition-all duration-200"
                                >
                                  <svg className="h-2 w-2" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2.5} aria-hidden="true">
                                    <path strokeLinecap="round" strokeLinejoin="round" d="M12 6v12m6-6H6" />
                                  </svg>
                                </button>
                                <ProductPrice product={p} size="xs" className="hidden sm:inline-flex truncate" />
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    );
                  })()}
                </div>
              )}

              {activeTab === "reviews" && (
                <div className="space-y-6">
                  {reviews.length === 0 ? (
                    <p className="text-gray-500 text-sm text-center py-8">No reviews yet. Be the first!</p>
                  ) : (
                    <div>
                      <div className="flex items-center justify-between mb-4">
                        <h3 className="text-sm font-semibold text-gray-950 uppercase tracking-wider">Customer Reviews</h3>
                        <div className="flex items-center gap-1.5">
                          <StarRating rating={Math.round(reviews.reduce((s, r) => s + r.rating, 0) / reviews.length)} />
                          <span className="text-gray-500 text-xs">({reviews.length})</span>
                        </div>
                      </div>
                      <div className="space-y-4">
                        {reviews.map((review) => (
                          <div key={review.id} className="border-b border-gray-200 pb-4 last:border-0">
                            <div className="flex items-center gap-2 mb-1">
                              <span className="text-gray-800 text-xs font-medium">{maskEmail(review.author)}</span>
                              {review.verified && <span className="text-[9px] bg-green-50 text-green-700 border border-green-200 px-1.5 py-0.5 rounded-full">Verified</span>}
                              <span className="text-gray-400 text-[10px] ml-auto">{new Date(review.created_at).toLocaleDateString()}</span>
                            </div>
                            <StarRating rating={review.rating} />
                            <p className="text-gray-600 text-xs mt-1.5 leading-relaxed">{review.comment}</p>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  <div ref={reviewFormRef} className="border-t border-gray-200 pt-5">
                    <h4 className="text-xs font-semibold text-gray-950 uppercase tracking-wider mb-3">Leave a Review</h4>
                    <StarRatingInput value={newReview.rating} onChange={(rating) => setNewReview({ ...newReview, rating })} />

                    {!user && (
                      <div className="grid grid-cols-2 gap-2 mt-3">
                        <input
                          type="text"
                          placeholder="Your name"
                          value={guestInfo.name}
                          onChange={(e) => setGuestInfo({ ...guestInfo, name: e.target.value })}
                          className="bg-gray-50 border border-gray-200 rounded-xl p-2.5 text-sm text-gray-800 placeholder-gray-400 focus:outline-none focus:border-amber-400 focus:ring-1 focus:ring-amber-400/20 transition-colors"
                        />
                        <input
                          type="email"
                          placeholder="Your email"
                          value={guestInfo.email}
                          onChange={(e) => setGuestInfo({ ...guestInfo, email: e.target.value })}
                          className="bg-gray-50 border border-gray-200 rounded-xl p-2.5 text-sm text-gray-800 placeholder-gray-400 focus:outline-none focus:border-amber-400 focus:ring-1 focus:ring-amber-400/20 transition-colors"
                        />
                        {/* Honeypot: hidden from real visitors, bots tend to fill every field */}
                        <input
                          type="text"
                          name="website"
                          value={guestInfo.website}
                          onChange={(e) => setGuestInfo({ ...guestInfo, website: e.target.value })}
                          className="hidden"
                          tabIndex="-1"
                          autoComplete="off"
                        />
                      </div>
                    )}

                    <textarea
                      className="w-full mt-3 bg-gray-50 border border-gray-200 rounded-xl p-3 text-sm text-gray-800 placeholder-gray-400 focus:outline-none focus:border-amber-400 focus:ring-1 focus:ring-amber-400/20 transition-colors resize-none"
                      rows="3"
                      placeholder="Share your experience..."
                      value={newReview.comment}
                      onChange={(e) => setNewReview({ ...newReview, comment: e.target.value })}
                    />
                    <button
                      onClick={submitReview}
                      disabled={submittingReview}
                      className="mt-2.5 px-5 py-2 rounded-xl bg-gradient-to-r from-amber-400 to-yellow-500 text-black font-bold text-xs uppercase tracking-wider hover:from-amber-500 hover:to-yellow-600 transition-all active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {submittingReview ? "Submitting…" : "Submit Review"}
                    </button>
                    <p className="text-[10px] text-gray-500 mt-1.5">Reviews are moderated before appearing publicly.</p>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default ProductPage;
